import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchOrder, updateOrder } from '../services/orderService';
import { toast } from 'react-toastify';
import { fetchShops } from '../services/shopService';
import { fetchCustomers } from '../services/customerService';
import { fetchCustomerAddresses } from '../services/customerAddress';

export default function EditOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shops: [],
    note: '',
    totalAmount: 0,
    deliveryCharge: '',
  });
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '' });
  const [addressForm, setAddressForm] = useState({
    label: '',
    address: '',
    latitude: '',
    longitude: '',
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [shopOptions, setShopOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const calculateTotal = (shops) =>
    shops.reduce((sum, shop) => {
      const shopTotal = shop.items.reduce((subtotal, item) => {
        const qty = Number(item.quantity) || 0;
        const unit = Number(item.unitPrice) || 0;
        return subtotal + qty * unit;
      }, 0);
      return sum + shopTotal;
    }, 0);

  const orderSubtotal = useMemo(() => calculateTotal(form.shops), [form.shops]);
  const parsedDeliveryCharge = Number(form.deliveryCharge);
  const deliveryChargeAmount = Number.isNaN(parsedDeliveryCharge) ? 0 : parsedDeliveryCharge;
  const computedFinalAmount = orderSubtotal + deliveryChargeAmount;

  const ensureCustomerOptions = useCallback((options) => {
    setCustomerOptions((prev) => {
      const map = new Map();
      [...prev, ...options]
        .filter((opt) => opt && opt.id)
        .forEach((opt) => {
          const id = Number(opt.id);
          if (!Number.isFinite(id)) return;
          map.set(id, {
            id,
            name: opt.name || `Customer #${id}`,
            phone: opt.phone || '',
          });
        });
      const list = Array.from(map.values());
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return list;
    });
  }, []);

  const ensureShopOptions = (options) => {
    setShopOptions((prev) => {
      const map = new Map();
      [...prev, ...options]
        .filter((opt) => opt && opt.id)
        .forEach((opt) => {
          map.set(opt.id, { id: opt.id, name: opt.name || `Shop #${opt.id}` });
        });
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchOrder(id);
        const o = res.data?.data || {};
        const shops = (o.shops || []).map((shop) => ({
          key: `existing-${shop.id}`,
          orderShopId: shop.id,
          shopId: shop.shopId,
          shopName: shop.shop?.name || `Shop #${shop.shopId}`,
          items: (shop.items || []).map((it) => ({
            key: `existing-item-${it.id}`,
            id: it.id,
            productName: it.productName || '',
            quantity: it.quantity != null ? it.quantity : 0,
            unitPrice: it.unitPrice != null ? it.unitPrice : 0,
            productId: it.productId || null,
            productVariantId: it.productVariantId || null,
          })),
        }));
        ensureShopOptions(
          shops.map((shop) => ({ id: shop.shopId, name: shop.shopName })),
        );
        const total = calculateTotal(shops);
        setForm({
          shops,
          note: o.note || '',
          totalAmount: total,
          deliveryCharge:
            o.deliveryCharge != null ? String(o.deliveryCharge) : '',
        });
        setCustomerForm({
          name: o.customer?.name || '',
          phone: o.customer?.phone || '',
        });
        if (o.customer?.id) {
          ensureCustomerOptions([
            { id: o.customer.id, name: o.customer.name, phone: o.customer.phone },
          ]);
          setSelectedCustomerId(String(o.customer.id));
        } else {
          setSelectedCustomerId('');
        }
        setAddressForm({
          label: o.customerAddress?.label || '',
          address: o.customerAddress?.address || '',
          latitude:
            o.customerAddress?.latitude != null
              ? String(o.customerAddress.latitude)
              : '',
          longitude:
            o.customerAddress?.longitude != null
              ? String(o.customerAddress.longitude)
              : '',
        });
        if (o.customerAddress?.id) {
          setSelectedAddressId(String(o.customerAddress.id));
        } else {
          setSelectedAddressId('');
        }
      } catch (err) {
        toast.error('Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [ensureCustomerOptions, id]);

  useEffect(() => {
    const loadShops = async () => {
      try {
        const res = await fetchShops({ limit: 200, page: 1, sortBy: 'name', sortOrder: 'ASC' });
        const records = res.data?.data?.records || [];
        ensureShopOptions(records.map((r) => ({ id: r.id, name: r.name })));
      } catch (err) {
        /* ignore */
      }
    };
    loadShops();
  }, []);

  useEffect(() => {
    const loadCustomers = async () => {
      setCustomerLoading(true);
      try {
        const res = await fetchCustomers({
          limit: 200,
          page: 1,
          sortBy: 'name',
          sortOrder: 'ASC',
        });
        const records = res.data?.data?.records || res.data?.data || res.data || [];
        ensureCustomerOptions(
          records.map((customer) => ({
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
          })),
        );
      } catch (err) {
        toast.error('Failed to load customers');
      } finally {
        setCustomerLoading(false);
      }
    };
    loadCustomers();
  }, [ensureCustomerOptions]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerForm({ name: '', phone: '' });
      return;
    }

    const numericId = Number(selectedCustomerId);
    if (!Number.isFinite(numericId)) {
      return;
    }

    const selected = customerOptions.find((opt) => opt.id === numericId);
    if (selected) {
      setCustomerForm({
        name: selected.name || '',
        phone: selected.phone || '',
      });
    }
  }, [customerOptions, selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setCustomerAddresses([]);
      setSelectedAddressId('');
      setAddressForm({ label: '', address: '', latitude: '', longitude: '' });
      return;
    }

    let ignore = false;
    const loadAddresses = async () => {
      setAddressLoading(true);
      try {
        const res = await fetchCustomerAddresses(selectedCustomerId);
        const records = res.data?.data?.records || res.data?.data || res.data || [];
        if (ignore) return;
        setCustomerAddresses(records);
        setSelectedAddressId((prev) => {
          if (prev && records.some((addr) => String(addr.id) === String(prev))) {
            return String(prev);
          }
          const preferred = records.find((addr) => addr.isDefault) || records[0];
          return preferred ? String(preferred.id) : '';
        });
      } catch (err) {
        if (!ignore) {
          toast.error('Failed to load customer addresses');
          setCustomerAddresses([]);
          setSelectedAddressId('');
        }
      } finally {
        if (!ignore) {
          setAddressLoading(false);
        }
      }
    };

    loadAddresses();
    return () => {
      ignore = true;
    };
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedAddressId) {
      setAddressForm({ label: '', address: '', latitude: '', longitude: '' });
      return;
    }

    const numericId = Number(selectedAddressId);
    if (!Number.isFinite(numericId)) {
      return;
    }

    const selected = customerAddresses.find((addr) => Number(addr.id) === numericId);
    if (selected) {
      setAddressForm({
        label: selected.label || '',
        address: selected.address || '',
        latitude:
          selected.latitude != null && selected.latitude !== ''
            ? String(selected.latitude)
            : '',
        longitude:
          selected.longitude != null && selected.longitude !== ''
            ? String(selected.longitude)
            : '',
      });
    }
  }, [customerAddresses, selectedAddressId]);

  const handleShopChange = (shopIdx, shopId) => {
    const numericShopId = shopId === '' ? '' : Number(shopId);
    const selectedOption = shopOptions.find((opt) => opt.id === numericShopId);
    setForm((prev) => {
      const shops = prev.shops.map((shop, idx) =>
        idx === shopIdx
          ? {
              ...shop,
              shopId: numericShopId,
              shopName: selectedOption?.name || shop.shopName,
            }
          : shop,
      );
      return { ...prev, shops };
    });
  };

  const handleItemChange = (shopIdx, itemIdx, field, value) => {
    setForm((prev) => {
      const shops = prev.shops.map((shop, sIdx) => {
        if (sIdx !== shopIdx) return shop;
        const items = shop.items.map((item, iIdx) =>
          iIdx === itemIdx ? { ...item, [field]: value } : item,
        );
        return { ...shop, items };
      });
      return {
        ...prev,
        shops,
        totalAmount: calculateTotal(shops),
      };
    });
  };

  const handleAddShop = () => {
    setForm((prev) => {
      const newShop = {
        key: `new-shop-${Date.now()}-${Math.random()}`,
        orderShopId: null,
        shopId: '',
        shopName: '',
        items: [
          {
            key: `new-item-${Date.now()}-${Math.random()}`,
            id: null,
            productName: '',
            quantity: 1,
            unitPrice: 0,
            productId: null,
            productVariantId: null,
          },
        ],
      };
      const shops = [...prev.shops, newShop];
      return { ...prev, shops, totalAmount: calculateTotal(shops) };
    });
  };

  const handleAddItem = (shopIdx) => {
    setForm((prev) => {
      const shops = prev.shops.map((shop, idx) => {
        if (idx !== shopIdx) return shop;
        const items = [
          ...shop.items,
          {
            key: `new-item-${Date.now()}-${Math.random()}`,
            id: null,
            productName: '',
            quantity: 1,
            unitPrice: 0,
            productId: null,
            productVariantId: null,
          },
        ];
        return { ...shop, items };
      });
      return { ...prev, shops, totalAmount: calculateTotal(shops) };
    });
  };

  const normalizedPayload = useMemo(
    () =>
      form.shops.map((shop) => ({
        orderShopId: shop.orderShopId,
        shopId: shop.shopId === '' ? null : Number(shop.shopId),
        items: shop.items
          .map((item) => ({
            id: item.id,
            productName: item.productName,
            quantity: Number(item.quantity) || 0,
            unitPrice: Number(item.unitPrice) || 0,
            productId: item.productId || null,
            productVariantId: item.productVariantId || null,
          }))
          .filter((item) => item.productName || item.quantity || item.unitPrice),
      })),
    [form.shops],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId) {
      toast.error('Select a customer');
      return;
    }
    const normalizedCustomerId = Number(selectedCustomerId);
    if (!Number.isFinite(normalizedCustomerId)) {
      toast.error('Invalid customer selected');
      return;
    }
    if (!selectedAddressId) {
      toast.error('Select a delivery address');
      return;
    }
    const normalizedAddressId = Number(selectedAddressId);
    if (!Number.isFinite(normalizedAddressId)) {
      toast.error('Invalid delivery address selected');
      return;
    }
    if (!customerForm.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!customerForm.phone.trim()) {
      toast.error('Customer phone is required');
      return;
    }
    const shopsPayload = normalizedPayload.filter((shop) =>
      shop.shopId && shop.items.length > 0,
    );
    if (!shopsPayload.length) {
      toast.error('Add at least one item');
      return;
    }
    try {
      setSaving(true);
      const parsedDeliveryCharge = Number(form.deliveryCharge);
      const deliveryChargeValue = Number.isNaN(parsedDeliveryCharge)
        ? 0
        : parsedDeliveryCharge;
      await updateOrder(id, {
        note: form.note,
        shops: shopsPayload,
        deliveryCharge: deliveryChargeValue,
        customerId: normalizedCustomerId,
        customerAddressId: normalizedAddressId,
        customer: {
          name: customerForm.name,
          phone: customerForm.phone,
        },
        customerAddress: {
          label: addressForm.label,
          address: addressForm.address,
          latitude: addressForm.latitude,
          longitude: addressForm.longitude,
        },
      });
      toast.success('Order updated');
      navigate(`/orders/${id}`);
    } catch (err) {
      toast.error('Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-4">Edit Order</h1>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border rounded p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-semibold">Customer Details</h2>
                  {customerLoading ? (
                    <span className="text-xs text-gray-500">Loading...</span>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="customer-select">
                    Customer
                  </label>
                  <select
                    id="customer-select"
                    value={selectedCustomerId}
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    className="w-full border px-3 py-2 rounded dark:bg-gray-800 dark:text-white"
                    disabled={customerLoading && customerOptions.length === 0}
                  >
                    <option value="">Select customer</option>
                    {customerOptions.map((customer) => {
                      const displayPhone = customer.phone ? ` (${customer.phone})` : '';
                      return (
                        <option key={customer.id} value={customer.id}>
                          {customer.name || `Customer #${customer.id}`}
                          {displayPhone}
                        </option>
                      );
                    })}
                  </select>
                  {customerOptions.length === 0 && !customerLoading ? (
                    <p className="mt-1 text-xs text-gray-500">
                      No customers available. Create a customer before editing an order.
                    </p>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="customer-name">
                      Name
                    </label>
                    <input
                      id="customer-name"
                      value={customerForm.name}
                      readOnly
                      className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="customer-phone">
                      Phone
                    </label>
                    <input
                      id="customer-phone"
                      value={customerForm.phone}
                      readOnly
                      className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
              <div className="border rounded p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-semibold">Delivery Address</h2>
                  {addressLoading ? (
                    <span className="text-xs text-gray-500">Loading...</span>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="address-select">
                    Address
                  </label>
                  <select
                    id="address-select"
                    value={selectedAddressId}
                    onChange={(e) => setSelectedAddressId(e.target.value)}
                    className="w-full border px-3 py-2 rounded dark:bg-gray-800 dark:text-white"
                    disabled={!selectedCustomerId || (addressLoading && customerAddresses.length === 0)}
                  >
                    <option value="">Select address</option>
                    {customerAddresses.map((addr) => {
                      const label = addr.label ? `${addr.label} — ` : '';
                      const addressLine = addr.address || 'Unnamed address';
                      return (
                        <option key={addr.id} value={addr.id}>
                          {`${label}${addressLine}`}
                        </option>
                      );
                    })}
                  </select>
                  {selectedCustomerId && customerAddresses.length === 0 && !addressLoading ? (
                    <p className="mt-1 text-xs text-gray-500">
                      No saved addresses for this customer.
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="address-label">
                    Label
                  </label>
                  <input
                    id="address-label"
                    value={addressForm.label}
                    readOnly
                    className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="address-text">
                    Address
                  </label>
                  <textarea
                    id="address-text"
                    value={addressForm.address}
                    readOnly
                    rows={4}
                    className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="address-lat">
                      Latitude
                    </label>
                    <input
                      id="address-lat"
                      value={addressForm.latitude}
                      readOnly
                      className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="address-lng">
                      Longitude
                    </label>
                    <input
                      id="address-lng"
                      value={addressForm.longitude}
                      readOnly
                      className="w-full border px-3 py-2 rounded bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-200 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {form.shops.map((shop, shopIdx) => (
                <div
                  key={shop.key || shop.orderShopId || shopIdx}
                  className="border rounded p-4 space-y-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Shop</label>
                      <select
                        value={shop.shopId ?? ''}
                        onChange={(e) => handleShopChange(shopIdx, e.target.value)}
                        className="w-full border px-3 py-2 rounded dark:bg-gray-800 dark:text-white"
                      >
                        <option value="">Select shop</option>
                        {shopOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {shop.orderShopId ? (
                      <span className="text-sm text-gray-500">
                        Order Shop ID: {shop.orderShopId}
                      </span>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-5 gap-2 font-semibold text-sm">
                      <div className="col-span-2">Product</div>
                      <div>Qty</div>
                      <div>Unit Price</div>
                      <div>Total</div>
                    </div>
                    {shop.items.map((it, itemIdx) => {
                      const quantity = Number(it.quantity) || 0;
                      const unitPrice = Number(it.unitPrice) || 0;
                      return (
                        <div
                          key={it.key || it.id || itemIdx}
                          className="grid grid-cols-5 gap-2 items-center"
                        >
                          <input
                            type="text"
                            value={it.productName}
                            onChange={(e) =>
                              handleItemChange(shopIdx, itemIdx, 'productName', e.target.value)
                            }
                            className="col-span-2 border px-2 py-1 rounded dark:bg-gray-800 dark:text-white"
                          />
                          <input
                            type="number"
                            value={it.quantity}
                            onChange={(e) =>
                              handleItemChange(shopIdx, itemIdx, 'quantity', e.target.value)
                            }
                            className="border px-2 py-1 rounded dark:bg-gray-800 dark:text-white"
                            min="0"
                            step="0.01"
                          />
                          <input
                            type="number"
                            value={it.unitPrice}
                            onChange={(e) =>
                              handleItemChange(shopIdx, itemIdx, 'unitPrice', e.target.value)
                            }
                            className="border px-2 py-1 rounded dark:bg-gray-800 dark:text-white"
                            min="0"
                            step="0.01"
                          />
                          <div className="text-sm font-medium">
                            {(quantity * unitPrice).toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => handleAddItem(shopIdx)}
                      className="text-sm text-indigo-600 hover:underline"
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleAddShop}
              className="text-sm text-indigo-600 hover:underline"
            >
              Add Shop
            </button>
            <div>
              <label className="block mb-1">Total Amount</label>
              <input
                type="number"
                value={orderSubtotal.toFixed(2)}
                readOnly
                className="w-full border px-3 py-2 rounded bg-gray-100 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block mb-1">Delivery Charge</label>
              <input
                type="number"
                value={form.deliveryCharge}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    deliveryCharge: e.target.value,
                  }))
                }
                className="w-full border px-3 py-2 rounded dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block mb-1">Final Amount</label>
              <input
                type="number"
                value={computedFinalAmount.toFixed(2)}
                readOnly
                className="w-full border px-3 py-2 rounded bg-gray-100 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block mb-1">Note</label>
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full border px-3 py-2 rounded dark:bg-gray-800 dark:text-white"
              />
            </div>
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        )}
      </div>
    </LayoutWithSidebar>
  );
}
